import { alpha, Box, useTheme } from '@mui/material';
import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { OrderData } from '../../api';
import { UNASSIGNED_ID } from '../views/dragndrop/DragDropTypes';

export type GeneratedResource = {
  id: string;
  name: string;
  index: number;
  schedulerConfigId: string;
  workshopColor?: string;
};

export enum ResourceDropState {
  VALID = 'valid',
  INVALID = 'invalid',
  UNAVAILABLE = 'unavailable',
  NEUTRAL = 'neutral',
}

type ResourceColumnProps = {
  resource: GeneratedResource | null;
  orders: Array<OrderData & { css: any }>;
  columnWidth: number;
  leftPosition: number;
  openOrder: (event: any, order: OrderData) => void;
  dropState?: ResourceDropState;
  previewSpan?: number; // Number of resources this assignment would span
};

export const ResourceColumn = ({
  resource,
  orders: _orders,
  columnWidth,
  leftPosition,
  openOrder: _openOrder,
  dropState = ResourceDropState.NEUTRAL,
  previewSpan = 1,
}: ResourceColumnProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: resource?.id || UNASSIGNED_ID,
  });

  const theme = useTheme();
  const validColor = theme.palette.primary.main;
  const invalidColor = theme.palette.error.main;

  const getBackgroundColor = () => {
    // Base workshop color with very light opacity
    const baseColor = resource?.workshopColor ? `${resource.workshopColor}18` : 'transparent';
    if (isOver) {
      switch (dropState) {
        case ResourceDropState.VALID:
          return alpha(validColor, 0.35); // More visible green for valid
        case ResourceDropState.INVALID:
          return alpha(invalidColor, 0.2); // Red for invalid
        case ResourceDropState.UNAVAILABLE:
          return alpha(invalidColor, 0.25); // Red for unavailable (same as invalid)
        default:
          return alpha(validColor, 0.2); // Light green default
      }
    }

    // Subtle hints when not hovering
    switch (dropState) {
      case ResourceDropState.VALID:
        return alpha(validColor, 0.15);
      case ResourceDropState.INVALID:
        return alpha(invalidColor, 0.05);
      case ResourceDropState.UNAVAILABLE:
        return alpha(invalidColor, 0.08); // Red hint for unavailable
      default:
        return baseColor; // Use base workshop color when no specific state
    }
  };

  // Get border style for multi-resource preview
  const getBorderStyle = () => {
    if (isOver && previewSpan > 1) {
      const color = dropState === ResourceDropState.VALID ? validColor : invalidColor;
      return `2px dashed ${color}`;
    }
    return undefined;
  };

  return (
    <Box
      id={'mmm'}
      ref={setNodeRef}
      data-calendar-slot="resource"
      sx={{
        width: `${columnWidth * previewSpan}%`, // Expand width for multi-resource preview
        insetInlineStart: `${leftPosition}%`,
        height: 50 * 17,
        position: 'absolute',
        borderInlineEnd: '1px solid',
        borderInlineEndColor: 'mb.lineSoft',
        backgroundColor: getBackgroundColor(),
        borderInlineStart: '1px solid',
        borderInlineStartColor: 'mb.lineSoft',
        border: getBorderStyle(),
        opacity: dropState === ResourceDropState.UNAVAILABLE || dropState === ResourceDropState.INVALID ? 0.8 : 1,
        cursor:
          dropState === ResourceDropState.UNAVAILABLE || dropState === ResourceDropState.INVALID
            ? 'not-allowed'
            : 'default',
        transition: 'all 0.2s ease-in-out',
      }}>
      {/* This column is just for drop zones - orders are rendered separately */}
    </Box>
  );
};
